import { getTagsByIds } from "./api";
import { transformGetTagsByIdsResponse } from "./transformers";
import type { Tag } from "./types";

/**
 * TagManager - Manages tag data with in-memory caching
 *
 * Features:
 * - Fetches tags by IDs with automatic deduplication
 * - Caches tags in memory to minimize API calls
 * - Hydrates Tag[] objects with missing name fields
 */
export class TagManager {
  private cache: Map<number, Tag> = new Map();
  private pendingRequests: Map<string, Promise<Tag[]>> = new Map();

  /**
   * Fetches tags from API and updates cache
   * @param ids - Array of tag IDs to fetch
   * @returns Promise resolving to array of Tags
   */
  private async fetchAndCache(ids: number[]): Promise<Tag[]> {
    if (ids.length === 0) return [];

    // Create a unique key for this request (space-separated to match API format)
    const requestKey = ids.sort().join(" ");

    // Check if there's already a pending request for these IDs
    if (this.pendingRequests.has(requestKey)) {
      return this.pendingRequests.get(requestKey)!;
    }

    // Create new request
    const request = (async () => {
      try {
        const response = await getTagsByIds(ids);
        const tags = transformGetTagsByIdsResponse(response);

        // Update cache with fetched tags
        tags.forEach((tag) => {
          this.cache.set(tag.tag_id, tag);
        });

        return tags;
      } finally {
        // Clean up pending request
        this.pendingRequests.delete(requestKey);
      }
    })();

    // Store pending request
    this.pendingRequests.set(requestKey, request);

    return request;
  }

  /**
   * Get tags by IDs with caching
   * - Deduplicates IDs
   * - Checks cache first
   * - Fetches missing tags from API
   * - Returns all requested tags
   *
   * @param ids - Array of tag IDs
   * @returns Promise resolving to array of Tags
   */
  async getTagsByIds(ids: number[]): Promise<Tag[]> {
    // Deduplicate IDs
    const uniqueIds = Array.from(new Set(ids));

    if (uniqueIds.length === 0) return [];

    // Separate cached and missing IDs
    const cachedTags: Tag[] = [];
    const missingIds: number[] = [];

    uniqueIds.forEach((id) => {
      const cached = this.cache.get(id);
      if (cached) {
        cachedTags.push(cached);
      } else {
        missingIds.push(id);
      }
    });

    // Fetch missing tags
    let fetchedTags: Tag[] = [];
    if (missingIds.length > 0) {
      fetchedTags = await this.fetchAndCache(missingIds);
    }

    // Combine and return all tags
    return [...cachedTags, ...fetchedTags];
  }

  /**
   * Hydrates Tag[] with missing name fields
   * - Identifies tags with missing or empty names
   * - Checks cache for those tags
   * - Fetches still missing tags from API
   * - Returns hydrated Tag[] in the same order
   *
   * @param tags - Array of Tags, some potentially with missing names
   * @returns Promise resolving to array of hydrated Tags
   */
  async hydrateTags(tags: Tag[]): Promise<Tag[]> {
    // Find tags that need hydration (missing or empty name)
    const tagsNeedingHydration = tags.filter(
      (tag) => !tag.name || tag.name.trim() === "" || tag.name === "[Tag label not available]"
    );

    if (tagsNeedingHydration.length === 0) {
      return tags; // No hydration needed
    }

    // Get IDs of tags needing hydration
    const idsToFetch = tagsNeedingHydration.map((tag) => tag.tag_id);

    // Fetch tags (will use cache when possible)
    const hydratedTags = await this.getTagsByIds(idsToFetch);

    // Create a map of hydrated tags for quick lookup
    const hydratedMap = new Map<number, Tag>();
    hydratedTags.forEach((tag) => {
      hydratedMap.set(tag.tag_id, tag);
    });

    // Return tags with hydrated names
    return tags.map((tag) => {
      const hydrated = hydratedMap.get(tag.tag_id);
      if (hydrated) {
        return {
          ...tag,
          name: hydrated.name,
        };
      }
      return tag;
    });
  }

  /**
   * Manually adds tags to the cache
   * Useful for pre-populating cache with tags from other sources
   *
   * @param tags - Array of Tags to add to cache
   */
  addToCache(tags: Tag[]): void {
    tags.forEach((tag) => {
      if (tag.tag_id && tag.name) {
        this.cache.set(tag.tag_id, tag);
      }
    });
  }

  /**
   * Clears the entire cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Removes specific tag IDs from cache
   *
   * @param ids - Array of tag IDs to remove
   */
  removeFromCache(ids: number[]): void {
    ids.forEach((id) => {
      this.cache.delete(id);
    });
  }

  /**
   * Gets the current size of the cache
   *
   * @returns Number of tags in cache
   */
  getCacheSize(): number {
    return this.cache.size;
  }

  /**
   * Checks if a tag ID exists in cache
   *
   * @param id - Tag ID to check
   * @returns Boolean indicating if tag is cached
   */
  isCached(id: number): boolean {
    return this.cache.has(id);
  }

  /**
   * Gets a tag from cache without fetching
   *
   * @param id - Tag ID
   * @returns Tag if cached, undefined otherwise
   */
  getCached(id: number): Tag | undefined {
    return this.cache.get(id);
  }
}

// Export a singleton instance
export const tagManager = new TagManager();
